// POS shift (növbə / günlük kassa) gateway-i — cloud-un tarix-əsaslı kassa
// hesabatını pos_local-un shift-əsaslı modelinə bağlayır.
//
// Cloud ↔ pos_local fərqləri (vacib):
//   • cloud-da "növbə aç" anlayışı yoxdur; GunlukKasa yalnız GET /report + POST
//     /payments/end-of-day işlədir. pos_local isə shift-əsaslıdır (open → close).
//   • pos_local-da ödəniş yalnız AÇIQ shift-ə bağlanır (yoxdursa shiftId=null və
//     report-da görünmür). Ona görə ödəniş zamanı `ensureOpenShift` çağırılır —
//     shift yoxdursa openingCash=0 ilə avtomatik açılır.
//   • Endirim üçün qeyd olunan `other` ödənişləri (bax paymentGateway) real pul
//     deyil → kassa report-undan və grand_total-dan xaric edilir.

import { posRequest } from './posClient';

// ── tiplər ───────────────────────────────────────────────────────────────────
type PosMethod = 'cash' | 'card' | 'wallet' | 'voucher' | 'other';

interface PosShiftState {
  id: string;
  status: 'open' | 'closed';
  openingCashCents: number;
  /** epoch ms — tarix aralığına görə süzmək üçün (`/v1/shifts/recent`). */
  openedAt: number;
}

interface PosPaymentRow {
  id: string;
  orderId: string;
  method: PosMethod;
  amountCents: number;
  status: 'captured' | 'voided' | 'refund';
  paidAt: number; // epoch ms
  userId?: string | null; // ödənişi qeyd edən (paidByUser)
}

interface PosOrderInfo {
  openedAt?: number;
  closedAt?: number | null;
  openedByUser?: string | null;
  closedByUser?: string | null;
}

/** Müddəti oxunaqlı formata çevir (SÜRE sütunu). */
function formatDuration(startMs?: number, endMs?: number | null): string {
  if (!startMs || !endMs || endMs < startMs) return '-';
  const totalMin = Math.round((endMs - startMs) / 60000);
  if (totalMin < 1) return '<1dəq';
  if (totalMin < 60) return `${totalMin}dəq`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `${h}s ${m}dəq` : `${h}s`;
}

/** İşçi adını ID ilə həll etmək üçün front-un staff_list-i (localStorage). */
function readStaffMap(): Record<string, string> {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('staff_list') : null;
    if (!raw) return {};
    const list = JSON.parse(raw) as Array<{ id?: string | number; name?: string }>;
    const map: Record<string, string> = {};
    for (const s of list) if (s?.id != null && s.name) map[String(s.id)] = s.name;
    return map;
  } catch {
    return {};
  }
}

interface MethodBucket {
  count: number;
  netCents: number;
  tipsCents: number;
}

interface PosShiftBreakdown {
  byMethod: Partial<Record<PosMethod, MethodBucket>>;
  totalNetCents: number;
  totalTipsCents: number;
  cashDrawerCents: number;
}

interface PosXReport {
  cash: { expectedCents: number };
}

interface GatewayResult {
  status: number;
  data: unknown;
}

// ── köməkçilər ────────────────────────────────────────────────────────────────
function posError(status: number, message: string, code: string): Error {
  const e = new Error(message) as Error & { posStatus?: number; posCode?: string };
  e.posStatus = status;
  e.posCode = code;
  return e;
}

function throwFromRes(res: { error?: { code: string; message: string }; _status?: number }): never {
  const status = (res as { _status?: number })._status ?? 400;
  throw posError(status, res.error?.message || 'POS shift xətası', res.error?.code || 'POS_ERROR');
}

/** pos_local method → cloud report `type`-i. */
function methodToCloudType(method: PosMethod): string {
  switch (method) {
    case 'cash':
      return 'cash';
    case 'card':
      return 'bank';
    case 'wallet':
      return 'customer_balance';
    default:
      return method; // voucher (other artıq xaric edilir)
  }
}

async function getCurrentShift(): Promise<PosShiftState | null> {
  const res = await posRequest<PosShiftState | null>({ method: 'GET', path: '/v1/shifts/current' });
  if (!res.ok) throwFromRes(res);
  return res.data && res.data.status === 'open' ? res.data : null;
}

/**
 * Açıq shift varsa id-sini qaytar; yoxdursa openingCash=0 ilə avtomatik aç.
 * Ödəniş axınları (paymentGateway) çağırır ki, ödənişlər shift-ə bağlansın.
 * Best-effort: açıla bilməsə null (ödəniş yenə də işləyir, sadəcə shiftsiz qeyd olunur).
 */
export async function ensureOpenShift(): Promise<string | null> {
  const current = await getCurrentShift();
  if (current) return current.id;
  const opened = await posRequest<PosShiftState>({
    method: 'POST',
    path: '/v1/shifts/open',
    body: { openingCashCents: 0 },
  });
  return opened.ok && opened.data ? opened.data.id : null;
}

function emptyReport(): Record<string, unknown> {
  return {
    payments: [],
    grand_total: 0,
    total_amount: 0,
    total_cash: 0,
    total_bank: 0,
    total_customer_balance: 0,
    payment_count: 0,
  };
}

export interface DailyReportRange {
  /** YYYY-MM-DD (daxil) */
  from?: string;
  /** YYYY-MM-DD (daxil) */
  to?: string;
}

/** "YYYY-MM-DD" → günün başlanğıcı/sonu (lokal vaxt) ms. */
function dayStartMs(d: string): number {
  return new Date(`${d}T00:00:00`).getTime();
}
function dayEndMs(d: string): number {
  return new Date(`${d}T23:59:59.999`).getTime();
}

/**
 * Hesabata daxil ediləcək shift-lər.
 *
 * Tarix aralığı VERİLMƏYİBSƏ → yalnız cari açıq shift (köhnə davranış, "bu günün kassası").
 * Verilibsə → `/v1/shifts/recent` siyahısından açılış tarixi aralığa düşənlər.
 * (recent son 20 shift-i qaytarır — bir restoran üçün təxminən son 20 gün.)
 */
async function shiftsForRange(range?: DailyReportRange): Promise<PosShiftState[]> {
  if (!range?.from && !range?.to) {
    const current = await getCurrentShift();
    return current ? [current] : [];
  }
  const res = await posRequest<PosShiftState[]>({ method: 'GET', path: '/v1/shifts/recent' });
  if (!res.ok) throwFromRes(res);
  const fromMs = range.from ? dayStartMs(range.from) : -Infinity;
  const toMs = range.to ? dayEndMs(range.to) : Infinity;
  return (res.data ?? []).filter((sh) => sh.openedAt >= fromMs && sh.openedAt <= toMs);
}

// ── GET /report və GET /payments → kassa hesabatı ──────────────────────────────
// Tarix filtri olmadan: cari açıq növbə. Filtrlə: aralığa düşən bütün növbələr.
export async function getDailyReport(range?: DailyReportRange): Promise<GatewayResult> {
  const shifts = await shiftsForRange(range);
  if (shifts.length === 0) return { status: 200, data: emptyReport() };

  const perShift = await Promise.all(
    shifts.map(async (sh) => {
      const [paysRes, sumRes] = await Promise.all([
        posRequest<PosPaymentRow[]>({ method: 'GET', path: `/v1/shifts/${sh.id}/payments` }),
        posRequest<PosShiftBreakdown>({ method: 'GET', path: `/v1/shifts/${sh.id}/payments/summary` }),
      ]);
      if (!paysRes.ok) throwFromRes(paysRes);
      if (!sumRes.ok) throwFromRes(sumRes);
      return { pays: paysRes.data ?? [], sum: sumRes.data };
    }),
  );

  // Real tender-lər: captured + other-dən başqa (other = endirim filler-i, pul deyil).
  const captured = perShift
    .flatMap((r) => r.pays)
    .filter((p) => p.status === 'captured' && p.method !== 'other');

  // SÜRE (açılış→bağlanış) və İŞÇİ üçün unikal sifarişləri çək (order user + vaxtlar).
  const orderIds = Array.from(new Set(captured.map((p) => p.orderId)));
  const orderMap: Record<string, PosOrderInfo> = {};
  await Promise.all(
    orderIds.map(async (oid) => {
      const o = await posRequest<PosOrderInfo>({ method: 'GET', path: `/v1/orders/${oid}` });
      if (o.ok && o.data) orderMap[oid] = o.data;
    }),
  );
  const staffMap = readStaffMap();

  const rows = captured.map((p) => {
    const o = orderMap[p.orderId];
    const openedMs = o?.openedAt;
    const closedMs = o?.closedAt ?? p.paidAt;
    const paidIso = new Date(p.paidAt).toISOString();
    // İşçi: sifarişi bağlayan / açan, ya da ödənişi qeyd edən user ID.
    const userId = o?.closedByUser ?? o?.openedByUser ?? p.userId ?? null;
    return {
      id: p.id,
      payment_id: p.id,
      order_id: p.orderId,
      method: p.method,
      type: methodToCloudType(p.method),
      amount: p.amountCents / 100,
      total_amount: p.amountCents / 100,
      paid_at: paidIso,
      date: paidIso,
      open_date: openedMs ? new Date(openedMs).toISOString() : paidIso,
      close_date: new Date(closedMs).toISOString(),
      duration: formatDuration(openedMs, o?.closedAt ?? p.paidAt),
      user_id: userId,
      user_name: (userId && staffMap[String(userId)]) || '-',
    };
  });

  // Bütün seçilmiş shift-lərin method breakdown-u cəmlənir.
  const sumNet = (key: 'cash' | 'card' | 'wallet') =>
    perShift.reduce((acc, r) => acc + ((r.sum?.byMethod ?? {})[key]?.netCents ?? 0), 0);
  const totalCash = sumNet('cash') / 100;
  const totalBank = sumNet('card') / 100;
  const totalCustomerBalance = sumNet('wallet') / 100;
  // grand_total = real toplanan pul (other/discount filler xaric).
  const grandTotal = totalCash + totalBank + totalCustomerBalance;

  return {
    status: 200,
    data: {
      payments: rows,
      grand_total: grandTotal,
      total_amount: grandTotal,
      total_cash: totalCash,
      total_bank: totalBank,
      total_customer_balance: totalCustomerBalance,
      payment_count: rows.length,
    },
  };
}

// ── POST /payments/end-of-day → cari shift-i bağla (gün sonu) ──────────────────
export async function runEndOfDay(): Promise<GatewayResult> {
  const shift = await getCurrentShift();
  if (!shift) {
    throw posError(409, 'Bağlanacaq açıq növbə yoxdur', 'NO_OPEN_SHIFT');
  }

  // X-report gözlənilən nağd kassanı verir → onu closing kimi göndəririk (variance 0).
  const x = await posRequest<PosXReport>({ method: 'GET', path: `/v1/shifts/${shift.id}/x-report` });
  const closingCashCents = x.ok && x.data ? x.data.cash?.expectedCents ?? shift.openingCashCents : shift.openingCashCents;

  const close = await posRequest({
    method: 'POST',
    path: `/v1/shifts/${shift.id}/close`,
    body: { closingCashCents },
  });
  if (!close.ok) throwFromRes(close);

  return { status: 200, data: { message: 'Gün sonu bağlandı' } };
}
